import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import {
    DigitaiAircraftStatus,
    DigitaiCreateFleetAircraft,
    DigitaiFleetAircraft,
    DigitaiFleetAircraftEngine,
    DigitaiUpdateFleetAircraftById,
    DigitaiUpdateFleetAircrafts,
} from '@apps/digitai';
import { AircraftStatusService } from '@apps/digitai/aircraft-status';
import {
    createMutation,
    deleteByIdMutation,
    deleteMutation,
    fields,
    findByIdQuery,
    findByIdWithRelationsQuery,
    findQuery,
    getQuery,
    insertMutation,
    paginationQuery,
    updateByIdMutation,
    updateMutation,
} from '@apps/digitai/fleet-aircraft';
import { FleetAircraftEngineService } from '@apps/digitai/fleet-aircraft-engine';
import {
    GraphQLHeaders,
    GraphQLService,
    GridData,
    parseGqlFields,
    QueryStatement,
} from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';
import { getRelations } from './fleet-aircraft.graphql';

@Injectable({
    providedIn: 'root',
})
export class FleetAircraftService {
    paginationSubject$: BehaviorSubject<GridData<DigitaiFleetAircraft> | null> =
        new BehaviorSubject(null);
    fleetAircraftSubject$: BehaviorSubject<DigitaiFleetAircraft | null> =
        new BehaviorSubject(null);
    fleetAircraftsSubject$: BehaviorSubject<DigitaiFleetAircraft[] | null> =
        new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: {
        [key: string]: BehaviorSubject<GridData<DigitaiFleetAircraft> | null>;
    } = {};
    fleetAircraftScoped: {
        [key: string]: BehaviorSubject<DigitaiFleetAircraft | null>;
    } = {};
    fleetAircraftsScoped: {
        [key: string]: BehaviorSubject<DigitaiFleetAircraft[] | null>;
    } = {};

    constructor(
        private readonly aircraftStatusService: AircraftStatusService,
        private readonly fleetAircraftEngineService: FleetAircraftEngineService,
        private readonly graphqlService: GraphQLService
    ) {}

    /**
     * Getters
     */
    get pagination$(): Observable<GridData<DigitaiFleetAircraft>> {
        return this.paginationSubject$.asObservable();
    }

    get fleetAircraft$(): Observable<DigitaiFleetAircraft> {
        return this.fleetAircraftSubject$.asObservable();
    }

    get fleetAircrafts$(): Observable<DigitaiFleetAircraft[]> {
        return this.fleetAircraftsSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(
        scope: string,
        pagination: GridData<DigitaiFleetAircraft>
    ): void {
        if (this.paginationScoped[scope]) {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(
        scope: string
    ): Observable<GridData<DigitaiFleetAircraft>> {
        if (!this.paginationScoped[scope])
            this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeFleetAircraft(scope: string, object: DigitaiFleetAircraft): void {
        if (this.fleetAircraftScoped[scope]) {
            this.fleetAircraftScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.fleetAircraftScoped[scope] = new BehaviorSubject(object);
    }

    getScopeFleetAircraft(scope: string): Observable<DigitaiFleetAircraft> {
        if (!this.fleetAircraftScoped[scope])
            this.fleetAircraftScoped[scope] = new BehaviorSubject(null);
        return this.fleetAircraftScoped[scope].asObservable();
    }

    setScopeFleetAircrafts(
        scope: string,
        objects: DigitaiFleetAircraft[]
    ): void {
        if (this.fleetAircraftsScoped[scope]) {
            this.fleetAircraftsScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.fleetAircraftsScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeFleetAircrafts(scope: string): Observable<DigitaiFleetAircraft[]> {
        if (!this.fleetAircraftsScoped[scope])
            this.fleetAircraftsScoped[scope] = new BehaviorSubject(null);
        return this.fleetAircraftsScoped[scope].asObservable();
    }

    pagination({
        graphqlStatement = paginationQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<GridData<DigitaiFleetAircraft>> {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiFleetAircraft> }>({
                query: graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data.pagination),
                tap((pagination) =>
                    scope
                        ? this.setScopePagination(scope, pagination)
                        : this.paginationSubject$.next(pagination)
                )
            );
    }

    findById({
        graphqlStatement = findByIdQuery,
        id = null,
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetAircraft;
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetAircraft;
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetAircraft(scope, data.object)
                        : this.fleetAircraftSubject$.next(data.object)
                )
            );
    }

    findByIdWithRelations({
        graphqlStatement = findByIdWithRelationsQuery,
        id = '',
        constraint = {},
        queryAircraftStatuses = {},
        constraintAircraftStatuses = {},
        queryPaginateFleetAircraftEngines = {},
        constraintPaginateFleetAircraftEngines = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        queryAircraftStatuses?: QueryStatement;
        constraintAircraftStatuses?: QueryStatement;
        queryPaginateFleetAircraftEngines?: QueryStatement;
        constraintPaginateFleetAircraftEngines?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetAircraft;
        digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
        digitaiPaginateFleetAircraftEngines: GridData<DigitaiFleetAircraftEngine>;
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetAircraft;
                digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
                digitaiPaginateFleetAircraftEngines: GridData<DigitaiFleetAircraftEngine>;
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryAircraftStatuses,
                    constraintAircraftStatuses,
                    queryPaginateFleetAircraftEngines,
                    constraintPaginateFleetAircraftEngines,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) => {
                    if (scope) {
                        this.setScopeFleetAircraft(scope, data.object);
                    } else {
                        this.fleetAircraftSubject$.next(data.object);
                    }
                    this.aircraftStatusService.aircraftStatusesSubject$.next(
                        data.digitaiGetAircraftStatuses
                    );
                    this.fleetAircraftEngineService.paginationSubject$.next(
                        data.digitaiPaginateFleetAircraftEngines
                    );
                })
            );
    }

    find({
        graphqlStatement = findQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetAircraft;
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetAircraft;
            }>({
                query: parseGqlFields(
                    graphqlStatement,
                    fields,
                    query,
                    constraint
                ),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetAircraft(scope, data.object)
                        : this.fleetAircraftSubject$.next(data.object)
                )
            );
    }

    get({
        graphqlStatement = getQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        objects: DigitaiFleetAircraft[];
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiFleetAircraft[];
            }>({
                query: parseGqlFields(
                    graphqlStatement,
                    fields,
                    query,
                    constraint
                ),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetAircrafts(scope, data.objects)
                        : this.fleetAircraftsSubject$.next(data.objects)
                )
            );
    }

    getRelations({
        queryAircraftStatuses = {},
        constraintAircraftStatuses = {},
        headers = {},
    }: {
        queryAircraftStatuses?: QueryStatement;
        constraintAircraftStatuses?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<{
        digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
            }>({
                query: getRelations,
                variables: {
                    queryAircraftStatuses,
                    constraintAircraftStatuses,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) => {
                    this.aircraftStatusService.aircraftStatusesSubject$.next(
                        data.digitaiGetAircraftStatuses
                    );
                })
            );
    }

    create<T>({
        graphqlStatement = createMutation,
        object = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiCreateFleetAircraft;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
            },
            context: {
                headers,
            },
        });
    }

    insert<T>({
        graphqlStatement = insertMutation,
        objects = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        objects?: DigitaiCreateFleetAircraft[];
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: objects,
            },
            context: {
                headers,
            },
        });
    }

    updateById<T>({
        graphqlStatement = updateByIdMutation,
        object = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiUpdateFleetAircraftById;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
            },
            context: {
                headers,
            },
        });
    }

    update<T>({
        graphqlStatement = updateMutation,
        object = null,
        query = {},
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiUpdateFleetAircrafts;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
                query,
                constraint,
            },
            context: {
                headers,
            },
        });
    }

    deleteById<T>({
        graphqlStatement = deleteByIdMutation,
        id = null,
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                id,
                constraint,
            },
            context: {
                headers,
            },
        });
    }

    delete<T>({
        graphqlStatement = deleteMutation,
        query = {},
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                query,
                constraint,
            },
            context: {
                headers,
            },
        });
    }
}
